import React, { useCallback, useEffect, useState } from "react";
import CustomizeExperienceSection from "./customize-experience-section";
import { BASE_CLASS, CustomizeExperienceStatus } from "./customize-experience";
import { t } from "utilities/localization-utils";
import PublicationRecord from "models/view-models/publication-record";
import PublicationCardSimple from "molecules/cards/publication-card-simple";
import usePublicationFavorites from "utilities/hooks/domain/user-publication-favorites/use-publication-favorites";
import useBook from "utilities/hooks/domain/books/use-book";
import PublicationService from "utilities/services/publications/publication-service";
import usePageErrors from "utilities/hooks/use-page-errors";
import { List } from "immutable";
import BookRecord from "models/view-models/book-record";
import { uniqBy } from "lodash";
import useUnauthorizedResult from "utilities/hooks/use-unauthorized-result";
import UserConfigurationErrorKeys from "constants/user-configuration-error-keys";
import UserService from "utilities/services/users/user-service";
import { useGlobalState } from "utilities/contexts/use-global-state-context";

interface CustomizeExperienceFavoritesProps {
    onContinue: () => void;
}

const TAKE = 5;

const buildFavoritableList = (
    allPublications: PublicationRecord[],
    featuredBooks: List<BookRecord>
) => {
    const findFeaturedTopicBooks = allPublications.filter((p) =>
        featuredBooks.some((f) => p.code === f.code && p.edition === f.edition)
    );
    const publicationsWithFeaturedFirst = uniqBy(
        [...findFeaturedTopicBooks, ...allPublications],
        "id"
    );
    return publicationsWithFeaturedFirst.slice(0, TAKE);
};

const CustomizeExperienceFavorites = ({
    onContinue,
}: CustomizeExperienceFavoritesProps) => {
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [publications, setPublications] = useState<Array<PublicationRecord>>(
        []
    );

    const { favorites, setFavorites } = usePublicationFavorites();
    const { handlePageLoadError } = usePageErrors();

    const { resultObject: featuredBooks } = useBook({ isNew: true });
    const { list: listPublications } = PublicationService.useList();
    const { patch: completedOnboardingFavorites } =
        UserService.useOnboardingFavoritesCompletedOn();
    const [completedOnboarding, setCompletedOnboarding] = useState(false);

    //clear any unauthorized results
    useUnauthorizedResult(
        UserConfigurationErrorKeys.ERROR_FAVORITES_NOT_INITIALIZED,
        UserConfigurationErrorKeys.ERROR_TEAM_NOT_INITIALIZED
    );
    const { globalState } = useGlobalState();
    const currentUserId = globalState.currentIdentity?.userId();
    // Get all featured publications
    const findFeaturedPublications = useCallback(
        async (
            featuredBooks: List<BookRecord>
        ): Promise<Array<PublicationRecord>> => {
            if (!completedOnboarding) return [];
            try {
                const response = await listPublications();
                const allPublications = response.resultObjects ?? [];
                const featuredPublications = allPublications.filter(
                    (p: PublicationRecord) => {
                        return featuredBooks.some(
                            (b: BookRecord) =>
                                b.code === p.code && b.edition === p.edition
                        );
                    }
                );
                return featuredPublications;
            } catch (result) {
                handlePageLoadError(result);
                return [];
            }
        },
        [handlePageLoadError, listPublications, completedOnboarding]
    );

    useEffect(() => {
        const completeOnboardingFavorites = async () => {
            try {
                await completedOnboardingFavorites({
                    id: currentUserId!,
                });
                setCompletedOnboarding(true);
            } catch (err) {
                handlePageLoadError(err);
            }
        };

        completeOnboardingFavorites();
    }, []);

    // Query for topic-related pubs, and then if none, then query featured publications
    useEffect(() => {
        async function getPublications() {
            setShowLoader(true);

            const publications = await findFeaturedPublications(featuredBooks);
            setPublications(publications);
            setShowLoader(false);
        }

        getPublications();
    }, [featuredBooks, findFeaturedPublications]);

    const availablePublications = buildFavoritableList(
        publications,
        featuredBooks
    );

    return (
        <CustomizeExperienceSection
            heading={t("selectFavoritesPage-header")}
            subheading={t("customizeExperience-selectFavorites-subheader")}
            stage={CustomizeExperienceStatus.SelectFavorites}
            isLoading={showLoader}
            onContinueClick={onContinue}
            disableContinueButton={false}>
            <div className={`${BASE_CLASS}__publications`}>
                <div className={`${BASE_CLASS}__publications__list`}>
                    {availablePublications.map(
                        (publication: PublicationRecord) => {
                            return (
                                <PublicationCardSimple
                                    favorites={favorites}
                                    key={publication.id}
                                    onChangeFavorites={setFavorites}
                                    publication={publication}
                                />
                            );
                        }
                    )}
                </div>
            </div>
        </CustomizeExperienceSection>
    );
};

export default CustomizeExperienceFavorites;
